import { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../constants/config";
import { toast } from "react-hot-toast";
import { getFullName } from "../../../utils/urlUtils";
import { format, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Eye, Trash2, KeyRound, Calendar, UserX, Clock, BarChart2 } from "lucide-react";
import { formatAddress, getDepartmentNames, getWingNames, getDesignationName } from "./hooks/useTeachers";

const ATTENDANCE_LEGEND = [
  { status: "present", label: "Present", color: "bg-green-100 text-green-800 border-green-200" },
  { status: "absent", label: "Absent", color: "bg-red-100 text-red-800 border-red-200" },
  { status: "late", label: "Late", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  { status: "holiday", label: "Holiday", color: "bg-gray-100 text-gray-600 border-gray-200" },
];

const getAttendanceStatusColor = (status) => {
  switch (status) {
    case "present": return "bg-green-100 text-green-800 border-green-200";
    case "absent": return "bg-red-100 text-red-800 border-red-200";
    case "late": return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "holiday": return "bg-gray-100 text-gray-600 border-gray-200";
    default: return "bg-white border-gray-200";
  }
};

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
    <span className="text-gray-600 font-medium">{label}</span>
    <span className="text-gray-800">{value || "Not provided"}</span>
  </div>
);

function AttendanceCalendar({ teacher, setPresentCount, setAbsentCount, setLateCount }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [teacherAttendance, setTeacherAttendance] = useState([]);
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  useEffect(() => {
    if (!teacher) return;
    const fetchAttendance = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get(API_ENDPOINTS.teacherAttendance, {
          params: {
            teacherId: teacher._id,
            startDate: format(monthStart, "yyyy-MM-dd"),
            endDate: format(monthEnd, "yyyy-MM-dd"),
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = response.data.data.attendance;
        setTeacherAttendance(data);
        const present = data.filter((d) => d.status === "present").length;
        const absent = data.filter((d) => d.status === "absent").length;
        const late = data.filter((d) => d.status === "late").length;
        const total = present + absent + late;
        if (total > 0) {
          setPresentCount(Math.round((present / total) * 100));
          setAbsentCount(Math.round((absent / total) * 100));
          setLateCount(Math.round((late / total) * 100));
        } else {
          setPresentCount(0);
          setAbsentCount(0);
          setLateCount(0);
        }
      } catch (error) {
        console.error("Failed to fetch teacher attendance:", error);
      }
    };
    fetchAttendance();
  }, [currentDate, teacher]);

  const getAttendanceStatus = (date) => {
    if (date.getDay() === 0) return "holiday";
    const dateStr = format(date, "yyyy-MM-dd");
    const record = teacherAttendance.find(
      (r) => format(new Date(r.date), "yyyy-MM-dd") === dateStr
    );
    return record ? record.status : null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{format(currentDate, "MMMM yyyy")}</h3>
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))}>Previous</Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))}>Next</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 mb-2">
        {ATTENDANCE_LEGEND.map(({ status, label, color }) => (
          <div key={status} className="flex items-center space-x-2">
            <div className={`w-4 h-4 rounded border ${color}`}></div>
            <span className="text-sm text-gray-600">{label}</span>
          </div>
        ))}
      </div>
      <div className="inline-block min-w-full border rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="text-center py-1 text-xs font-medium text-gray-600 bg-gray-50">{day}</div>
          ))}
          {days.map((day) => {
            const status = getAttendanceStatus(day);
            return (
              <Popover key={day.toString()}>
                <PopoverTrigger asChild>
                  <button className={`aspect-square w-full p-1 border-t border-l first:border-l-0 ${getAttendanceStatusColor(status)} hover:opacity-75 transition-opacity`}>
                    <div className="text-center"><span className="text-xs">{format(day, "d")}</span></div>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-48">
                  <div className="p-2">
                    <p className="font-medium text-sm">{format(day, "EEEE, MMMM d, yyyy")}</p>
                    <p className="text-sm capitalize mt-1">Status: <span className="font-medium">{status || "No record"}</span></p>
                  </div>
                </PopoverContent>
              </Popover>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MonthlyTrendsChart({ teacher }) {
  const [monthlyData, setMonthlyData] = useState([]);

  useEffect(() => {
    if (!teacher) return;
    const fetchMonthlyData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get(API_ENDPOINTS.teacherAttendanceMonthlySummary, {
          params: { teacherId: teacher._id },
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = response?.data?.data?.monthlySummary || [];
        setMonthlyData(data.map((item) => {
          const [year, month] = item.month.split("-");
          return {
            month: new Date(`${year}-${month}-01`).toLocaleString("default", { month: "short" }),
            present: item.present || 0,
            absent: item.absent || 0,
            late: item.late || 0,
          };
        }));
      } catch (error) {
        console.error("Error fetching monthly attendance summary:", error);
      }
    };
    fetchMonthlyData();
  }, [teacher]);

  return (
    <div id="chart-container" className="w-full overflow-x-auto">
      <div style={{ width: "100%", height: 400 }}>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="present" fill="#10B981" name="Present" />
            <Bar dataKey="absent" fill="#EF4444" name="Absent" />
            <Bar dataKey="late" fill="#F59E0B" name="Late" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function TeacherProfile({ teacher, leaves, activeTab, setActiveTab, onEdit, onDelete, onPermissions, fetchTeachers }) {
  const [presentCount, setPresentCount] = useState(0);
  const [absentCount, setAbsentCount] = useState(0);
  const [lateCount, setLateCount] = useState(0);

  const renderLeaves = () => (
    <div className="mt-4">
      <h3 className="text-lg font-semibold mb-2">Leave Applications</h3>
      {leaves.length === 0 ? (
        <p className="text-gray-500">No leaves records available.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leaves.map((leave) => (
              <TableRow key={leave._id}>
                <TableCell>{new Date(leave.startDate).toLocaleDateString()}</TableCell>
                <TableCell>{new Date(leave.endDate).toLocaleDateString()}</TableCell>
                <TableCell>{leave.type}</TableCell>
                <TableCell>{leave.status}</TableCell>
                <TableCell>{leave.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4 sm:p-6 rounded-t-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="relative group">
              <img
                src={teacher.profileImage || "/teacher_profile.png" || "/placeholder.svg"}
                alt={getFullName(teacher)}
                className="w-20 h-20 rounded-full border-4 border-white object-cover"
              />
              <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></span>
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="w-7 h-7 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      const fileInput = document.createElement("input");
                      fileInput.type = "file";
                      fileInput.accept = "image/*";
                      fileInput.onchange = async (event) => {
                        const file = event.target.files[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append("file", file);
                        try {
                          const token = localStorage.getItem("token");
                          await axiosInstance.put(API_ENDPOINTS.user(teacher._id), formData, {
                            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
                          });
                          toast.success("Profile image updated successfully");
                          fetchTeachers();
                        } catch (err) {
                          console.error("Failed to update profile image:", err);
                          toast.error("Failed to update profile image");
                        }
                      };
                      fileInput.click();
                    }}
                  >
                    <Pencil className="w-3 h-3 text-gray-700" />
                  </button>
                  <button
                    type="button"
                    className="w-7 h-7 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (teacher.profileImage) window.open(teacher.profileImage, "_blank");
                    }}
                  >
                    <Eye className="w-3 h-3 text-gray-700" />
                  </button>
                </div>
              </div>
            </div>
            <div className="text-white">
              <h2 className="text-2xl font-bold">
                {getFullName(teacher)}
                {teacher.username && (
                  <span className="ml-2 text-lg font-normal text-blue-100">@{teacher.username}</span>
                )}
              </h2>
              <p className="text-blue-100">
                {teacher.subject} Teacher - Class {teacher.class}
                {teacher.section && ` - ${teacher.section}`}
              </p>
              {teacher.classTeacherDetails && (
                <p className="text-yellow-200 font-semibold">
                  Class Teacher: Class {teacher.classTeacherDetails.className} - Section {teacher.classTeacherDetails.sectionName}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <Button variant="secondary" className="bg-white/10 hover:bg-white/20 text-white" onClick={onEdit}>
              <Pencil className="w-4 h-4 mr-2" /> Edit Profile
            </Button>
            <Button variant="secondary" className="bg-white/10 hover:bg-white/20 text-white" onClick={onPermissions}>
              <KeyRound className="w-4 h-4 mr-2" /> Permissions
            </Button>
            <Button variant="destructive" className="bg-red-500/10 hover:bg-red-500/20 text-white" onClick={onDelete}>
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b">
        <div className="flex flex-wrap px-4 sm:px-6">
          {["profile", "payroll", "leaves", "attendance"].map((tab) => (
            <Button
              key={tab}
              variant="ghost"
              className={`px-6 py-3 -mb-px ${activeTab === tab ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500"}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1).replace(/([A-Z])/g, " $1")}
            </Button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4 sm:p-6">
        {activeTab === "profile" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Card>
              <CardHeader><CardTitle className="text-lg font-semibold text-gray-800">Basic Information</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <InfoRow label="Role" value="Teacher" />
                <InfoRow label="Teacher Code" value={teacher.employeeCode || teacher.teacherCode} />
                <InfoRow label="Designation" value={getDesignationName(teacher.designation)} />
                <InfoRow label="Department" value={getDepartmentNames(teacher)} />
                <InfoRow label="Wing" value={getWingNames(teacher)} />
                <InfoRow label="Date of Birth" value={teacher.birthday ? new Date(teacher.birthday).toLocaleDateString() : null} />
                <InfoRow label="Blood Group" value={teacher.bloodGroup} />
                <InfoRow label="Gender" value={teacher.gender} />
                <InfoRow label="Date of Joining" value={teacher.dateOfJoining ? new Date(teacher.dateOfJoining).toLocaleDateString() : null} />
                <InfoRow label="Employment Type" value={teacher.teacherType || "Not provided"} />
                <InfoRow label="Contract Type" value={teacher.contractType || "Not provided"} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-lg font-semibold text-gray-800">Contact Details</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <InfoRow label="Email" value={teacher.email} />
                <InfoRow label="Phone Number" value={teacher.phoneNumber} />
                <InfoRow label="Aadhaar Card" value={teacher.aadhaarCard} />
                <InfoRow label="Address" value={formatAddress(teacher.address)} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-lg font-semibold text-gray-800">Teaching Details</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <InfoRow
                  label="Subjects"
                  value={teacher.subjects?.length > 0 ? teacher.subjects.map((s) => s.name).join(", ") : "Not assigned"}
                />
                <InfoRow label="Primary Class" value={teacher.class || "Not assigned"} />
                <InfoRow label="Section" value={teacher.section || "Not assigned"} />
                {teacher.classTeacherDetails && (
                  <InfoRow
                    label="Class Teacher Of"
                    value={`Class ${teacher.classTeacherDetails.className} - Section ${teacher.classTeacherDetails.sectionName}`}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "payroll" && (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-4" />
            <p>No payroll information available.</p>
          </div>
        )}

        {activeTab === "leaves" && renderLeaves()}

        {activeTab === "attendance" && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="col-span-1 lg:col-span-4">
                <Card className="shadow-md hover:shadow-lg transition-shadow duration-200">
                  <CardHeader className="border-b pb-3">
                    <CardTitle className="text-lg font-semibold flex items-center">
                      <Calendar className="w-5 h-5 mr-2 text-blue-600" /> Attendance Calendar
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <AttendanceCalendar
                      teacher={teacher}
                      setPresentCount={setPresentCount}
                      setAbsentCount={setAbsentCount}
                      setLateCount={setLateCount}
                    />
                  </CardContent>
                </Card>
              </div>
              <div className="col-span-1 space-y-4 grid grid-cols-3 lg:grid-cols-1 gap-4 lg:gap-0">
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-none">
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center">
                      <div className="text-green-600 mb-2"><Calendar className="h-8 w-8" /></div>
                      <p className="text-3xl font-bold text-green-700">{presentCount}%</p>
                      <p className="text-sm text-green-600 mt-1">Present Days</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-50 to-red-100 border-none">
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center">
                      <div className="text-red-600 mb-2"><UserX className="h-8 w-8" /></div>
                      <p className="text-3xl font-bold text-red-700">{absentCount}%</p>
                      <p className="text-sm text-red-600 mt-1">Absent Days</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-none">
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center">
                      <div className="text-yellow-600 mb-2"><Clock className="h-8 w-8" /></div>
                      <p className="text-3xl font-bold text-yellow-700">{lateCount}%</p>
                      <p className="text-sm text-yellow-600 mt-1">Late Arrivals</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            <Card className="shadow-md hover:shadow-lg transition-shadow duration-200">
              <CardHeader className="border-b pb-3">
                <CardTitle className="text-lg font-semibold flex items-center">
                  <BarChart2 className="w-5 h-5 mr-2 text-blue-600" /> Monthly Attendance Trends
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <MonthlyTrendsChart teacher={teacher} />
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
